import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Plus, RotateCcw } from "lucide-react";
import { ActivityStepDraft, createActivityStep } from "../activitySteps/activityStepTypes";
import { parseActivitySteps } from "../activitySteps/activityStepsParser";

import {
  detectActivityStepListStyle,
  serializeActivitySteps,
} from "../activitySteps/activityStepsSerializer";

import { activityStepsAreReady } from "../activitySteps/activityStepsValidation";
import { navigateWithinPortal } from "../routing/portalRoutes";
import { ActivityStepEditorCard } from "./ActivityStepEditorCard";

function discardConfirmed(): boolean {
  return window.confirm("Discard your unsaved Activity Steps changes?");
}

export function ActivityStepsEditor({
  initialValue,
  sourceKind,
  returnPath,
  fallbackUrl,
  onSave,
}: {
  initialValue: string;
  sourceKind: "template" | "homework";
  returnPath: string;
  fallbackUrl: string;
  onSave: (activitySteps: string) => Promise<void>;
}) {
  const [listStyle] = useState(() =>
    detectActivityStepListStyle(initialValue),
  );
  
  const [steps, setSteps] = useState(() =>
    parseActivitySteps(initialValue),
  );
  
  const [savedValue, setSavedValue] = useState(() =>
    serializeActivitySteps(parseActivitySteps(initialValue), listStyle),
  );
  
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  const draftValue = useMemo(
    () => serializeActivitySteps(steps, listStyle),
    [steps, listStyle],
  );

  const ready = activityStepsAreReady(steps);
  const dirty = draftValue !== savedValue;

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  const updateStep = (index: number, next: ActivityStepDraft) => {
    setSaveError(null);
    setSteps((current) => current.map((step, itemIndex) => (itemIndex === index ? next : step)));
  };

  const moveStep = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= steps.length) return;
    setSaveError(null);
    setSteps((current) => {
      const reordered = [...current];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      return reordered;
    });
  };

  const leaveEditor = () => {
    if (dirty && !discardConfirmed()) return;
    navigateWithinPortal(returnPath);
  };

  const resetDraft = () => {
    if (dirty && !discardConfirmed()) return;
    setSteps(parseActivitySteps(savedValue));
    setSaveError(null);
  };

  const save = async () => {
    if (!ready || !dirty || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(draftValue);
      setSavedValue(draftValue);
      setSaving(false);
      navigateWithinPortal(returnPath);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "Activity Steps could not be saved.",
      );
      setSaving(false);
    }
  };

  const status = saving
    ? "Saving"
    : !ready
      ? "Needs Info"
      : dirty
        ? "Unsaved"
        : "Saved";

  return (
    <div className="cp-steps-editor">
      <div className="cp-steps-editor__toolbar">
        <button type="button" className="cp-button cp-button--secondary" onClick={leaveEditor}>
          <ArrowLeft aria-hidden="true" /> Back
        </button>
        <div className="cp-steps-editor__summary">
          <span>{steps.length} {steps.length === 1 ? "step" : "steps"}</span>
          <span className="cp-step-save-status" data-status={status.toLowerCase().replace(" ", "-")}>
            {status}
          </span>
        </div>
      </div>

      <div className="cp-steps-editor__workspace">
        <section className="cp-steps-editor__draft" aria-labelledby="activity-steps-draft-title">
          <div className="cp-content-section__header">
            <div>
              <h2 id="activity-steps-draft-title">Activity Steps</h2>
              <p>Choose a step type, then edit and arrange the instructions.</p>
            </div>
            <button
              type="button"
              className="cp-button cp-button--secondary"
              onClick={() => setSteps((current) => [...current, createActivityStep()])}
            >
              <Plus aria-hidden="true" /> Add Step
            </button>
          </div>

          <div className="cp-step-editor-list">
            {steps.map((step, index) => (
              <ActivityStepEditorCard
                key={step.id}
                step={step}
                index={index}
                count={steps.length}
                onChange={(next) => updateStep(index, next)}
                onRemove={() => setSteps((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                onMove={(direction) => moveStep(index, index + direction)}
                onDropAt={moveStep}
              />
            ))}
          </div>
        </section>

        <aside className="cp-steps-editor__preview">
          <section className="cp-steps-preview-card cp-steps-preview-card--draft">
            <h2>Draft Preview</h2>
            <p className="cp-steps-preview-card__help">
              This matches the saved Activity Steps field.
            </p>
            <pre>{draftValue || "No steps yet."}</pre>
          </section>
          <section className="cp-steps-preview-card cp-steps-preview-card--existing">
            <h2>Existing Activity Steps</h2>
            <p className="cp-steps-preview-card__help">
              Current value stored on this {sourceKind === "template" ? "Activity Template" : "Homework Assignment"}.
            </p>
            <pre>{savedValue.trim() || "No existing steps."}</pre>
            <button type="button" className="cp-button cp-button--secondary" onClick={resetDraft}>
              <RotateCcw aria-hidden="true" /> Reset Draft
            </button>
          </section>
          <details className="cp-steps-editor__fallback">
            <summary>Need the legacy editor?</summary>
            <a href={fallbackUrl} target="_blank" rel="noreferrer">Open legacy Steps Editor</a>
          </details>
        </aside>
      </div>

      {saveError ? <div className="cp-form-error" role="alert">{saveError}</div> : null}

      <div className="cp-form-actions cp-form-actions--sticky cp-steps-editor__actions">
        <button type="button" className="cp-button cp-button--secondary" onClick={leaveEditor} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="cp-button cp-button--primary" onClick={save} disabled={!dirty || !ready || saving}>
          {saving ? "Saving…" : "Save & Return"}
        </button>
      </div>
    </div>
  );
}
